// Topic endpoints.
//
// The list and detail shapes differ server-side:
//   getAllTopics     -> { data: { topics: [ raw rows keyed topicId ] } }
//   get_topic_by_id  -> { data: { id, topicName, subject, semester,
//                                 quizzes, assignments, materials } }
// Its nested `materials` are truncated to id + title, so materials are fetched
// separately — see getTopic below.

import { get, post, patch, del } from './client';
import { listByTopic, listAll } from './materials';
import { listAssignments, listQuizzes } from './coursework';
import { toUiTopic, toUiMaterial, topicToApi } from './adapters';

/**
 * GET /topic/getAllTopics — scoped to the caller's group by the server ('all' for
 * owners). The rows carry no contents, only totals.
 *
 * Those totals are refined with ONE extra request. The server counts every
 * material row under `materialsCount`, but a topic card shows PDFs and recorded
 * sessions as separate figures, and the two are told apart by the document's
 * extension — a split a single total can't express. getAllMaterials returns every
 * row for the group in one go, so the counts are derived from it rather than by
 * opening each topic.
 *
 * If that request fails the server's own total stands, so the cards degrade to a
 * combined figure rather than to nothing.
 */
export async function listTopics() {
  const [data, materials] = await Promise.all([
    get('/topic/getAllTopics'),
    listAll().catch(() => null),
  ]);
  const rows = Array.isArray(data?.topics) ? data.topics : [];
  return rows.map((row) => {
    const topic = toUiTopic(row);
    if (!Array.isArray(materials)) return topic;
    const mine = materials.filter((m) => m.topicId === topic.id);
    return {
      ...topic,
      materialsCount: mine.filter((m) => m.kind === 'pdf').length,
      videosCount: mine.filter((m) => m.kind === 'url').length,
    };
  });
}

/**
 * The topic with everything the topic page shows.
 *
 * NONE of get_topic_by_id's nested arrays are usable: every one of them is
 * fetched by a data-layer function commented "only return id and name", so the
 * rows come back stripped —
 *   materials   attributes: [['materialId','id'], 'title']
 *   assignments attributes: ['assignId', ['assignId','id'], 'title','startDate','mark']
 *   quizzes     attributes: ['quizId', ['quizId','id'], 'title','mark']
 * No document, no due date (`endDate`), no subject, no submission state. Relying
 * on them turned PDFs into "recorded sessions" and left assignments with no due
 * date, no paper to open and a wrong status.
 *
 * So each collection comes from its own full-row endpoint and is filtered to
 * this topic. All four requests run together, and any one of them failing only
 * empties its own section.
 */
export async function getTopic(topicId) {
  const id = String(topicId);
  const [data, materials, assignments, quizzes] = await Promise.all([
    get(`/topic/get_topic_by_id/${encodeURIComponent(topicId)}`),
    listByTopic(topicId).catch(() => []),
    listAssignments().then((r) => r.items).catch(() => []),
    listQuizzes().catch(() => []),
  ]);
  const topic = toUiTopic(data);
  /**
   * The subject is stamped on from the TOPIC, because the coursework endpoints
   * don't send one: getAllAssignments and getAllQuizzes return no `subject` field
   * at all, so every card was tinted and chipped as Biology (the adapter's
   * fallback). Subject is a property of the topic server-side, and this is the one
   * place that knows it for certain.
   */
  const withSubject = (rows) => rows.map((r) => ({ ...r, subj: topic.subj }));
  return {
    ...topic,
    // Already adapted, so they bypass toUiTopic's own splitting.
    materials: materials.filter((m) => m.kind === 'pdf'),
    videos: materials.filter((m) => m.kind === 'url'),
    assignments: withSubject(assignments.filter((a) => a.topicId === id)),
    quizzes: withSubject(quizzes.filter((q) => q.topicId === id)),
  };
}

// POST /topic/createTopic — the server takes `group` from the admin's token,
// so it is not sent. Returns the created topic in the UI shape.
export async function createTopic(form) {
  const data = await post('/topic/createTopic', topicToApi(form));
  return toUiTopic(data);
}

// PATCH /topic/updateTopic/:topicId — accepts topicName / semester / subject.
export async function updateTopic(topicId, patchFields) {
  const body = {};
  if (patchFields.title !== undefined) body.topicName = patchFields.title;
  if (patchFields.semester !== undefined) body.semester = patchFields.semester;
  if (patchFields.subj !== undefined) body.subject = topicToApi(patchFields).subject;
  const data = await patch(`/topic/updateTopic/${encodeURIComponent(topicId)}`, body);
  return toUiTopic(data);
}

export const deleteTopic = (topicId) =>
  del(`/topic/deleteTopic/${encodeURIComponent(topicId)}`);

// Re-exported so the classroom page can adapt a single material after a create
// without reaching into the adapters module directly.
export { toUiMaterial };
